import { Box, Button } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { browserTimeZone } from '@/utils/time-zone';
import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { useEventStore } from '@/state/stores/use-event-store';
import { useNotifications } from '@/hooks/use-notifications';
import { useUpdateEventMutation } from '@/state/mutations/events/use-update-event.ts';
import { updateEventSchemaFactory } from '@/schemas/event/update-event';
import type { z } from 'zod';
import { useNavigate } from 'react-router';
import GeneralForm from './tabs/general.form';
import CustomizeForm from './tabs/customize.form';
import SponsorsForm from './tabs/sponsors.form';
import ScheduleForm from './tabs/schedule.form';
import { useUpdateEventSettingsMutation } from '@/state/mutations/events/use-update-event-settings.ts';
import type { UpdateEventSettingsForm } from '@/schemas/event/update-event-settings';
import type { UpdateEventSponsorsForm } from '@/schemas/event/update-event-sponsors';
import { useGetEventSettingsQuery } from '@/state/queries/events/use-get-event-settings';
// import Email from './tabs/email';
import SpacesForm from './tabs/spaces.form';
import StandardModal from '@/components/common/standard.modal';
import {
  Brush,
  Settings,
  Business,
  EventNote,
  Gavel,
  PermMedia,
  ViewList,
  PlayCircleOutline,
} from '@mui/icons-material';
import { EventPlayoutSettingsPanel } from '@/components/playout/event-playout-settings-panel';
import EventMediaSettingsForm from './tabs/event-media-settings.form';
import EventSessionTypesSettingsForm from './tabs/event-session-types-settings.form';
import { getObjectId } from '@/helpers/object.id.helper';
import LegalTermsForm from './tabs/legal-terms.form';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useFeatureAccess } from '@/hooks/use-feature-access';

export type MenuButtonType =
  | 'general'
  | 'schedule'
  | 'spaces'
  | 'email'
  | 'customize'
  | 'sponsors'
  | 'legalTerms'
  | 'media'
  | 'sessionTypes'
  | 'playout';

const menuButtons = [
  {
    icon: <Settings />,
    key: 'general' as const,
    translationKey: 'event.detail.settingsMenu.general.title',
  },
  {
    icon: <EventNote />,
    key: 'schedule' as const,
    translationKey: 'event.detail.settingsMenu.schedule.title',
  },
  {
    icon: <Brush />,
    key: 'customize' as const,
    translationKey: 'event.detail.settingsMenu.customize.title',
  },
  {
    icon: <Business />,
    key: 'sponsors' as const,
    translationKey: 'event.detail.settingsMenu.sponsors.title',
  },
  {
    icon: <Gavel />,
    key: 'legalTerms' as const,
    translationKey: 'event.detail.settingsMenu.legalTerms.title',
  },
  {
    icon: <PermMedia />,
    key: 'media' as const,
    translationKey: 'event.detail.settingsMenu.media.title',
  },
  {
    icon: <ViewList />,
    key: 'sessionTypes' as const,
    translationKey: 'event.detail.settingsMenu.sessionTypes.title',
  },
  {
    icon: <PlayCircleOutline />,
    key: 'playout' as const,
    translationKey: 'event.detail.settingsMenu.playout.title',
  },
  // TODO: mvp remove spaces and email
  //{
  //  icon: <LocationCity />,
  //  key: 'spaces' as const,
  //  translationKey: 'event.detail.settingsMenu.spaces.title',
  //},
  //{
  //  icon: <EmailIcon />,
  //  key: 'email' as const,
  //  translationKey: 'event.detail.settingsMenu.email.title',
  //},
];

export default function EditSettingsModal({
  open,
  menuSelected,
  setMenuSelected,
  close,
  initialDayIndex,
}: {
  menuSelected: MenuButtonType;
  setMenuSelected: React.Dispatch<React.SetStateAction<MenuButtonType>>;
  open: boolean;
  close: () => void;
  initialDayIndex?: number;
}) {
  const t = useTranslations();
  const event = useEventStore((s) => s.event);
  const navigate = useNavigate();
  const isWideScreen = useBreakpointUp('md');
  const { hasAccess: hasPerformAccess, isLoading: performAccessLoading } = useFeatureAccess('perform');
  const { hasAccess: hasSponsoringAccess, isLoading: sponsoringAccessLoading } = useFeatureAccess('sponsoring');
  const showCustomizeTab = !performAccessLoading && hasPerformAccess;
  const showSponsorsTab = !sponsoringAccessLoading && hasSponsoringAccess;
  const showMediaTab = event?.multiFlow === true;
  const showPlayoutTab = event?.multiFlow === true;
  const settingsMenuButtons = useMemo(
    () =>
      menuButtons.filter(
        (btn) =>
          (btn.key !== 'customize' || showCustomizeTab) &&
          (btn.key !== 'sponsors' || showSponsorsTab) &&
          (btn.key !== 'media' || showMediaTab) &&
          (btn.key !== 'playout' || showPlayoutTab),
      ),
    [showCustomizeTab, showSponsorsTab, showMediaTab, showPlayoutTab],
  );
  const [customizeSettings, setCustomizeSettings] = useState<UpdateEventSettingsForm | null>(null);
  const [sponsorsSettings, setSponsorsSettings] = useState<UpdateEventSponsorsForm | null>(null);

  // Fetch current event settings
  const eventId = event?.origin ? getObjectId(event.origin) : event?.id;
  const { data: eventSettings } = useGetEventSettingsQuery(open ? eventId : undefined);

  // Set initial customize settings when data is loaded (render-time update)
  const [prevEventSettings, setPrevEventSettings] = useState(eventSettings);
  if (prevEventSettings !== eventSettings && eventSettings) {
    setPrevEventSettings(eventSettings);
    setCustomizeSettings({
      backgroundType: eventSettings.backgroundType,
      backgroundColor: eventSettings.backgroundColor,
      textColor: eventSettings.textColor,
      // Files are undefined since we can't convert URLs back to File objects
      backgroundImage: undefined,
      eventLogoImage: undefined,
      deleteEventLogo: false,
      remoteSpeakerBackgroundImage: undefined,
      deleteRemoteSpeakerBackground: false,
      backgroundOverlay: eventSettings.backgroundOverlay,
      backgroundOverlayColor: eventSettings.backgroundOverlayColor,
      backgroundOverlayTransparency: eventSettings.backgroundOverlayTransparency,
      fontSizeMultiplier: eventSettings.fontSizeMultiplier,
      liveTranslationFontSizeMultiplier:
        eventSettings.liveTranslationFontSizeMultiplier ?? eventSettings.fontSizeMultiplier,
      liveTranslationTextColor: eventSettings.liveTranslationTextColor,
      margins: eventSettings.margins,
    });
  }

  const notifications = useNotifications();

  const updateEventSchema = updateEventSchemaFactory(t);
  type UpdateEvent = z.infer<typeof updateEventSchema>;

  const { mutateAsync } = useUpdateEventMutation({ onSuccess: close });
  const { mutateAsync: mutateEventSettings } = useUpdateEventSettingsMutation();

  const form = useForm<UpdateEvent>({
    resolver: zodResolver(updateEventSchema),
    mode: 'onChange',
    defaultValues: {
      id: event?.id,
      title: event?.title ?? '',
      description: event?.description ?? '',
      location: event?.location ?? '',
      days: event?.days ?? [],
      spaces:
        event?.spaces?.map((space) => ({
          ...space,
          capacity: space.capacity,
          minTimeGap: space.minTimeGap,
        })) ?? [],
      startDate: event?.startDate,
      endDate: event?.endDate,
      timezone: event?.timezone ?? browserTimeZone(),
      multiFlow: event?.multiFlow ?? false,
      durationDays: event?.durationDays ?? 1,
      type: event?.type ?? 'CONFERENCE',
      version: event?.version,
    },
  });

  useEffect(() => {
    if (event) {
      form.reset({
        id: event.id,
        title: event.title ?? '',
        description: event.description ?? '',
        location: event.location ?? '',
        days: event.days ?? [],
        spaces:
          event.spaces?.map((space) => ({
            ...space,
            capacity: space.capacity,
            minTimeGap: space.minTimeGap,
          })) ?? [],
        multiFlow: event.multiFlow ?? false,
        startDate: event.startDate,
        endDate: event.endDate,
        // Events created before the field existed take this browser's zone until it is saved.
        timezone: event.timezone ?? browserTimeZone(),
        durationDays: event.durationDays ?? 1,
        type: event.type ?? 'CONFERENCE',
        version: event.version,
      });
    }
  }, [event, form]);

  useEffect(() => {
    if (!open) return;
    if (menuSelected === 'customize' && !performAccessLoading && !hasPerformAccess) {
      setMenuSelected('general');
    }
    if (menuSelected === 'sponsors' && !sponsoringAccessLoading && !hasSponsoringAccess) {
      setMenuSelected('general');
    }
    if (menuSelected === 'media' && !showMediaTab) {
      setMenuSelected('general');
    }
  }, [
    open,
    menuSelected,
    performAccessLoading,
    hasPerformAccess,
    sponsoringAccessLoading,
    hasSponsoringAccess,
    showMediaTab,
    setMenuSelected,
  ]);

  const handleMenuSelect = (key: MenuButtonType) => {
    if (!form.formState.isValid && form.formState.isDirty) return;
    setMenuSelected(key);
  };

  const onSubmit = async (data: UpdateEvent) => {
    if (!event?.id) return;

    // Track original multiFlow value to detect changes
    const originalMultiFlow = event.multiFlow ?? false;
    const newMultiFlow = data.multiFlow ?? false;

    try {
      await mutateAsync(data);

      // Submit customize and sponsors settings if they exist
      if (customizeSettings || sponsorsSettings) {
        const eventId = event.origin ? getObjectId(event.origin) : event.id;
        await mutateEventSettings({
          eventId,
          backgroundType: customizeSettings?.backgroundType || 'color',
          backgroundColor:
            customizeSettings?.backgroundType === 'color' ? customizeSettings.backgroundColor || undefined : undefined,
          textColor: customizeSettings?.textColor || undefined,
          backgroundImage:
            customizeSettings?.backgroundType === 'image' ? customizeSettings.backgroundImage : undefined,
          eventLogoImage: customizeSettings?.eventLogoImage,
          deleteEventLogo:
            customizeSettings?.deleteEventLogo === true && !customizeSettings?.eventLogoImage ? true : undefined,
          remoteSpeakerBackgroundImage: customizeSettings?.remoteSpeakerBackgroundImage,
          deleteRemoteSpeakerBackground:
            customizeSettings?.deleteRemoteSpeakerBackground === true &&
            !customizeSettings?.remoteSpeakerBackgroundImage
              ? true
              : undefined,
          backgroundOverlay: customizeSettings?.backgroundOverlay,
          backgroundOverlayColor: customizeSettings?.backgroundOverlayColor,
          backgroundOverlayTransparency: customizeSettings?.backgroundOverlayTransparency,
          fontSizeMultiplier: customizeSettings?.fontSizeMultiplier,
          liveTranslationFontSizeMultiplier: customizeSettings?.liveTranslationFontSizeMultiplier,
          liveTranslationTextColor: customizeSettings?.liveTranslationTextColor,
          margins: customizeSettings?.margins,
          sponsors: sponsorsSettings?.sponsors || [],
        });
      }

      notifications.notifySuccess(t('event.detail.dashboard.settings.tabs.customize.notification.updateSuccess'));

      // Handle navigation based on multiFlow changes
      if (originalMultiFlow !== newMultiFlow) {
        if (newMultiFlow) {
          // Changed to multi-flow: navigate to dashboard tab
          const eventId = event.origin ? (typeof event.origin === 'string' ? event.origin : event.origin.id) : event.id;
          navigate(`/event/${eventId}?tab=dashboard`);
        } else {
          // Changed to single-flow: navigate to flow details (existing behavior)
          // This would need to be implemented if there's a flow to navigate to
          close();
        }
      } else {
        close();
      }
    } catch {
      notifications.notifyError(t('event.detail.dashboard.settings.tabs.customize.notification.updateError'));
    }
  };

  const renderActiveTab = () => {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', pr: 1 }}>
        {menuSelected === 'general' && <GeneralForm form={form} />}
        {menuSelected === 'schedule' && <ScheduleForm form={form} initialDayIndex={initialDayIndex} />}
        {menuSelected === 'spaces' && <SpacesForm form={form} />}
        {menuSelected === 'customize' && showCustomizeTab && (
          <CustomizeForm onSettingsChange={setCustomizeSettings} initialSettings={eventSettings ?? undefined} />
        )}
        {menuSelected === 'sponsors' && showSponsorsTab && (
          <SponsorsForm onSettingsChange={setSponsorsSettings} initialSettings={eventSettings ?? undefined} />
        )}
        {menuSelected === 'legalTerms' && <LegalTermsForm />}
        {menuSelected === 'media' && showMediaTab && eventId && (
          <EventMediaSettingsForm eventId={eventId} initialSettings={eventSettings ?? undefined} />
        )}
        {menuSelected === 'sessionTypes' && eventId && (
          <EventSessionTypesSettingsForm eventId={eventId} initialSettings={eventSettings ?? undefined} />
        )}
        {menuSelected === 'playout' && showPlayoutTab && eventId && (
          <EventPlayoutSettingsPanel eventId={eventId} multiFlow={event?.multiFlow} />
        )}
        {/* {menuSelected === 'email' && <Email />} */}
      </Box>
    );
  };

  const settingsOnlyTab = menuSelected === 'media' || menuSelected === 'sessionTypes' || menuSelected === 'playout';

  const footer = settingsOnlyTab ? undefined : (
    <>
      <Button
        onClick={close}
        variant="outlined"
        size="small"
        sx={{
          'color': 'warning.main',
          'borderColor': 'warning.main',
          '&:hover': { borderColor: 'warning.dark', backgroundColor: 'warning.light', color: 'warning.dark' },
        }}
      >
        {t('common.cancel')}
      </Button>
      <Button onClick={form.handleSubmit(onSubmit)} variant="contained" size="small">
        {t('common.save')}
      </Button>
    </>
  );

  const customizeWide = menuSelected === 'customize' && showCustomizeTab;

  return (
    <StandardModal<MenuButtonType>
      open={open}
      close={close}
      title={t('event.settings')}
      menuButtons={settingsMenuButtons}
      menuSelected={menuSelected}
      onMenuSelect={handleMenuSelect}
      footer={footer}
      size="lg"
      height={isWideScreen ? (customizeWide ? '860px' : '800px') : '600px'}
      maxHeight={isWideScreen ? 'calc(100vh - 64px)' : 'calc(100vh - 32px)'}
      width={isWideScreen ? (customizeWide ? '1200px' : '1000px') : undefined}
    >
      {renderActiveTab()}
    </StandardModal>
  );
}
